import React, { useState } from 'react';
import {
  VStack, Input, Textarea, Text, Box, Button, SimpleGrid,
  InputGroup, InputRightElement, useToast
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const RegistroContratista: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();

  const [tipoTramite, setTipoTramite] = useState(data?.tipoTramite || '');
  const [razonSocial, setRazonSocial] = useState(data?.razonSocial || '');
  const [representante, setRepresentante] = useState(data?.representante || '');
  const [rfc, setRfc] = useState(data?.rfc || '');
  const [telefono, setTelefono] = useState(data?.telefono || '');
  const [correo, setCorreo] = useState(data?.correo || '');
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [especialidades, setEspecialidades] = useState(data?.especialidades || '');
  const [experiencia, setExperiencia] = useState(data?.experiencia || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [acta, setActa] = useState(data?.acta || '');
  const [cedula, setCedula] = useState(data?.cedula || '');
  const [comprobante, setComprobante] = useState(data?.comprobante || '');
  const [cv, setCv] = useState(data?.cv || '');

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Verifica que la dirección sea válida.',
          status: 'error',
        });
      }
    });
  };

  const handleGuardar = async () => {
    const payload = {
      tipoTramite,
      razonSocial,
      representante,
      rfc,
      telefono,
      correo,
      direccion,
      lat,
      lng,
      especialidades,
      experiencia,
      observaciones,
      acta,
      cedula,
      comprobante,
      cv,
      fechaRegistro: new Date()
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/registro-contratista`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al registrar contratista');

      toast({
        title: 'Registro exitoso',
        description: 'El contratista fue registrado correctamente.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un problema al registrar.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={4} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={4}>
        
          <Input placeholder="Tipo de trámite" value={tipoTramite} onChange={(e) => setTipoTramite(e.target.value)} />
          <Input placeholder="Razón social" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} />
          <Input placeholder="Representante legal" value={representante} onChange={(e) => setRepresentante(e.target.value)} />
          <Input placeholder="RFC" value={rfc} onChange={(e) => setRfc(e.target.value)} />
          <Input placeholder="Teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          <Input placeholder="Correo" value={correo} onChange={(e) => setCorreo(e.target.value)} />
          </SimpleGrid>
          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>
          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
          
          <Input placeholder="Especialidades" value={especialidades} onChange={(e) => setEspecialidades(e.target.value)} />
          <Input type="number" placeholder="Años de experiencia" value={experiencia} onChange={(e) => setExperiencia(Number(e.target.value))} />
          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          <Input placeholder="Acta constitutiva (URL o nombre)" value={acta} onChange={(e) => setActa(e.target.value)} />
          <Input placeholder="Cédula profesional (URL o nombre)" value={cedula} onChange={(e) => setCedula(e.target.value)} />
          <Input placeholder="Comprobante de domicilio" value={comprobante} onChange={(e) => setComprobante(e.target.value)} />
          <Input placeholder="CV o portafolio" value={cv} onChange={(e) => setCv(e.target.value)} />
</SimpleGrid>
          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar' : 'Actualizar'}
          </Button>
        </>
      ) : (
        <>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text><strong>Razón social:</strong> {razonSocial}</Text>
              <Text><strong>RFC:</strong> {rfc}</Text>
              <Text><strong>Teléfono:</strong> {telefono}</Text>
              <Text><strong>Correo:</strong> {correo}</Text>
              <Text><strong>Dirección:</strong> {direccion}</Text>
              <Text><strong>Especialidades:</strong> {especialidades}</Text>
              <Text><strong>Experiencia:</strong> {experiencia} años</Text>
            </Box>
            <Box>
              <Text><strong>Representante:</strong> {representante}</Text>
              <Text><strong>Acta:</strong> {acta}</Text>
              <Text><strong>Cédula:</strong> {cedula}</Text>
              <Text><strong>Comprobante:</strong> {comprobante}</Text>
              <Text><strong>CV:</strong> {cv}</Text>
              {observaciones && <Text><strong>Observaciones:</strong> {observaciones}</Text>}
            </Box>
          </SimpleGrid>
          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default RegistroContratista;
